import { PlusIcon } from '@shopify/polaris-icons';
import { Page, Modal, TextField, Select } from '../components/forms';
import { useEffect, useState } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  Checkbox,
  DataTable,
  EmptyState,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface Zone {
  id: number;
  name: string;
}

interface WeightRule {
  id: number;
  name: string;
  zone_id: number | null;
  zone_name: string | null;
  min_weight_kg: number;
  max_weight_kg: number;
  additional_lead_days: number;
  surcharge_amount: number;
  surcharge_type: string;
  requires_special_vehicle: number;
  is_active: number;
  priority: number;
  applies_to: 'order' | 'daily_total';
}

export default function WeightRules() {
  const { fetchApi, error } = useApi();
  const [rules, setRules] = useState<WeightRule[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<WeightRule | null>(null);

  // Form states
  const [name, setName] = useState('');
  const [zoneId, setZoneId] = useState<string>('');
  const [minWeight, setMinWeight] = useState('0');
  const [maxWeight, setMaxWeight] = useState('');
  const [additionalLeadDays, setAdditionalLeadDays] = useState('0');
  const [surchargeAmount, setSurchargeAmount] = useState('0');
  const [surchargeType, setSurchargeType] = useState('fixed');
  const [requiresSpecialVehicle, setRequiresSpecialVehicle] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [priority, setPriority] = useState('0');
  const [appliesTo, setAppliesTo] = useState<'order' | 'daily_total'>('daily_total');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    const [rulesData, zonesData] = await Promise.all([
      fetchApi<{ rules: WeightRule[] }>('/delivery/weight-rules'),
      fetchApi<{ zones: Zone[] }>('/zones'),
    ]);

    if (rulesData?.rules) {
      setRules(rulesData.rules);
    }
    if (zonesData?.zones) {
      setZones(zonesData.zones);
    }
  }

  const openCreateModal = () => {
    setEditingRule(null);
    setName('');
    setZoneId('');
    setMinWeight('0');
    setMaxWeight('');
    setAdditionalLeadDays('0');
    setSurchargeAmount('0');
    setSurchargeType('fixed');
    setRequiresSpecialVehicle(false);
    setIsActive(true);
    setPriority('0');
    setAppliesTo('daily_total');
    setModalOpen(true);
  };

  const openEditModal = (rule: WeightRule) => {
    setEditingRule(rule);
    setName(rule.name);
    setZoneId(rule.zone_id?.toString() || '');
    setMinWeight(rule.min_weight_kg.toString());
    setMaxWeight(rule.max_weight_kg.toString());
    setAdditionalLeadDays(rule.additional_lead_days.toString());
    setSurchargeAmount(rule.surcharge_amount.toString());
    setSurchargeType(rule.surcharge_type);
    setRequiresSpecialVehicle(rule.requires_special_vehicle === 1);
    setIsActive(rule.is_active === 1);
    setPriority(rule.priority.toString());
    setAppliesTo(rule.applies_to || 'daily_total');
    setModalOpen(true);
  };

  const handleSave = async () => {
    const payload = {
      name,
      zone_id: zoneId ? parseInt(zoneId) : null,
      min_weight_kg: parseFloat(minWeight) || 0,
      max_weight_kg: parseFloat(maxWeight) || 0,
      additional_lead_days: parseInt(additionalLeadDays) || 0,
      surcharge_amount: parseFloat(surchargeAmount) || 0,
      surcharge_type: surchargeType,
      requires_special_vehicle: requiresSpecialVehicle ? 1 : 0,
      is_active: isActive ? 1 : 0,
      priority: parseInt(priority) || 0,
      applies_to: appliesTo,
    };

    if (editingRule) {
      await fetchApi(`/delivery/weight-rules/${editingRule.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
    } else {
      await fetchApi('/delivery/weight-rules', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    }

    setModalOpen(false);
    loadData();
  };

  const handleDelete = async (id: number) => {
    if (confirm('Are you sure you want to delete this weight rule?')) {
      await fetchApi(`/delivery/weight-rules/${id}`, {
        method: 'DELETE',
      });
      loadData();
    }
  };

  const handleToggleActive = async (rule: WeightRule) => {
    await fetchApi(`/delivery/weight-rules/${rule.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        ...rule,
        is_active: rule.is_active === 1 ? 0 : 1,
      }),
    });
    loadData();
  };

  const zoneOptions = [
    { label: 'All Zones (Default)', value: '' },
    ...zones.map(z => ({ label: z.name, value: z.id.toString() })),
  ];

  const surchargeTypeOptions = [
    { label: 'Fixed Amount', value: 'fixed' },
    { label: 'Percentage', value: 'percentage' },
  ];

  const appliesToOptions = [
    { label: 'Daily Total Weight', value: 'daily_total' },
    { label: 'Individual Order Weight', value: 'order' },
  ];

  const tableRows = rules.map(rule => [
    <Text as="span" variant="bodyMd" fontWeight="bold">{rule.name}</Text>,
    rule.zone_name || 'All Zones',
    `${rule.min_weight_kg}kg - ${rule.max_weight_kg}kg`,
    rule.applies_to === 'daily_total' ? <Badge>Daily Total</Badge> : <Badge tone="info">Per Order</Badge>,
    rule.additional_lead_days > 0 ? `+${rule.additional_lead_days} days` : '-',
    rule.surcharge_amount > 0
      ? rule.surcharge_type === 'percentage'
        ? `+${rule.surcharge_amount}%`
        : `+${rule.surcharge_amount}`
      : '-',
    rule.requires_special_vehicle === 1 ? <Badge tone="warning">Yes</Badge> : '-',
    rule.is_active === 1
      ? <Badge tone="success">Active</Badge>
      : <Badge tone="warning">Inactive</Badge>,
    <InlineStack gap="200" wrap={false}>
      <Button size="slim" onClick={() => openEditModal(rule)}>Edit</Button>
      <Button
        size="slim"
        tone={rule.is_active === 1 ? 'critical' : undefined}
        onClick={() => handleToggleActive(rule)}
      >
        {rule.is_active === 1 ? 'Disable' : 'Enable'}
      </Button>
      <Button size="slim" tone="critical" onClick={() => handleDelete(rule.id)}>Delete</Button>
    </InlineStack>,
  ]);

  return (
    <Page
      title="Weight-Based Delivery Rules"
      subtitle="Configure additional lead time and surcharges based on order weight"
      primaryAction={{
        icon: PlusIcon, content: 'Add Weight Rule',
        onAction: openCreateModal,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="p" variant="bodySm" tone="subdued">
                Weight rules let you automatically add extra lead time or surcharges based on weight.
                Rules can apply to the day's total delivery weight or individual order weights.
              </Text>

              {rules.length === 0 ? (
                <EmptyState
                  heading="No weight rules configured"
                  action={{
                    icon: PlusIcon, content: 'Add Weight Rule',
                    onAction: openCreateModal,
                  }}
                  image=""
                >
                  <p>Create weight rules to add extra delivery time or surcharges for heavy orders.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={[
                    'text',
                    'text',
                    'text',
                    'text',
                    'text',
                    'text',
                    'text',
                    'text',
                    'text',
                  ]}
                  headings={[
                    'Name',
                    'Zone',
                    'Weight Range',
                    'Applies To',
                    'Extra Lead Time',
                    'Surcharge',
                    'Special Vehicle',
                    'Status',
                    'Actions',
                  ]}
                  rows={tableRows}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Example Rules Section */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">Example Use Cases</Text>
              <BlockStack gap="200">
                <InlineStack gap="200" blockAlign="start">
                  <div style={{ width: 8, height: 8, backgroundColor: '#008060', borderRadius: '50%', marginTop: 6 }} />
                  <BlockStack gap="100">
                    <Text as="span" variant="bodyMd" fontWeight="bold">Heavy Items (100kg+)</Text>
                    <Text as="span" variant="bodySm" tone="subdued">
                      Add 2 extra days lead time and requires special vehicle for orders over 100kg
                    </Text>
                  </BlockStack>
                </InlineStack>
                <InlineStack gap="200" blockAlign="start">
                  <div style={{ width: 8, height: 8, backgroundColor: '#008060', borderRadius: '50%', marginTop: 6 }} />
                  <BlockStack gap="100">
                    <Text as="span" variant="bodyMd" fontWeight="bold">Bulk Orders (50-100kg)</Text>
                    <Text as="span" variant="bodySm" tone="subdued">
                      Add 1 extra day lead time for medium-weight orders
                    </Text>
                  </BlockStack>
                </InlineStack>
                <InlineStack gap="200" blockAlign="start">
                  <div style={{ width: 8, height: 8, backgroundColor: '#008060', borderRadius: '50%', marginTop: 6 }} />
                  <BlockStack gap="100">
                    <Text as="span" variant="bodyMd" fontWeight="bold">Remote Zone Surcharge</Text>
                    <Text as="span" variant="bodySm" tone="subdued">
                      Add a 15% surcharge for heavy orders to remote delivery zones
                    </Text>
                  </BlockStack>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Create/Edit Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingRule ? 'Edit Weight Rule' : 'Create Weight Rule'}
        primaryAction={{
          icon: editingRule ? undefined : PlusIcon, content: 'Save',
          onAction: handleSave,
          disabled: !name || !maxWeight,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Rule Name"
              requiredIndicator value={name}
              onChange={setName}
              autoComplete="off"
              placeholder="e.g. Heavy Items, Bulk Orders"

            />

            <Select
              label="Apply to Zone"
              options={zoneOptions}
              value={zoneId}
              onChange={setZoneId}
              helpText="Leave blank to apply to all zones"
            />

            <InlineStack gap="400">
              <TextField
                label="Min Weight (kg)"
                type="number"
                value={minWeight}
                onChange={setMinWeight}
                autoComplete="off"
              />
              <TextField
                label="Max Weight (kg)"
                type="number"
                requiredIndicator value={maxWeight}
                onChange={setMaxWeight}
                autoComplete="off"

              />
            </InlineStack>

            <Select
              label="Applies To"
              options={appliesToOptions}
              value={appliesTo}
              onChange={(v) => setAppliesTo(v as 'order' | 'daily_total')}
              helpText="Daily Total: checks combined weight of all deliveries for that day. Per Order: checks individual order weight."
            />

            <TextField
              label="Additional Lead Days"
              type="number"
              value={additionalLeadDays}
              onChange={setAdditionalLeadDays}
              autoComplete="off"
              helpText="Extra days to add to the delivery lead time"
            />

            <InlineStack gap="400">
              <TextField
                label="Surcharge Amount"
                type="number"
                value={surchargeAmount}
                onChange={setSurchargeAmount}
                autoComplete="off"
              />
              <Select
                label="Surcharge Type"
                options={surchargeTypeOptions}
                value={surchargeType}
                onChange={setSurchargeType}
              />
            </InlineStack>

            <Checkbox
              label="Requires special vehicle"
              checked={requiresSpecialVehicle}
              onChange={setRequiresSpecialVehicle}
              helpText="Flag orders that need a larger delivery vehicle"
            />

            <TextField
              label="Priority"
              type="number"
              value={priority}
              onChange={setPriority}
              autoComplete="off"
              helpText="Higher priority rules are applied first (default 0)"
            />

            <Checkbox
              label="Active"
              checked={isActive}
              onChange={setIsActive}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
